import React, { useState, useEffect } from 'react';
import { chatApi } from '../../api/chat';
import ReactMarkdown from 'react-markdown';
import './QueryForm.scss';

// Custom components for rendering Markdown
const MarkdownComponents = {
  // Customize how paragraphs are rendered
  p: ({ children }) => <p className="markdown-paragraph">{children}</p>,
  
  // Customize how lists are rendered
  ul: ({ children }) => <ul className="markdown-list">{children}</ul>,
  ol: ({ children }) => <ol className="markdown-list">{children}</ol>,
  
  // Customize how list items are rendered
  li: ({ children }) => <li className="markdown-list-item">{children}</li>,
  
  // Customize how headings are rendered
  h1: ({ children }) => <h1 className="markdown-heading">{children}</h1>,
  h2: ({ children }) => <h2 className="markdown-heading">{children}</h2>,
  h3: ({ children }) => <h3 className="markdown-heading">{children}</h3>,
};

function QueryForm({ roomId, onTitleChange }) {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const fetchMessages = async () => {
      const response = await chatApi.getRoomMessages(roomId);
      setMessages(response);
    };
    fetchMessages();
  }, [roomId]);

  useEffect(() => {
    const chatContainer = document.getElementById("chat-container");

    if (chatContainer) {
        chatContainer.scrollTop = chatContainer.scrollHeight;
    }
}, [messages]);

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const clearImage = () => {
    setSelectedImage(null);
    // Clear the file input
    const fileInput = document.getElementById('image-upload');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (message.trim() || selectedImage) {
      setMessages((prevMessages) => ({
        ...prevMessages,
        messages: [
          ...prevMessages.messages,
          { id: Date.now(), content: message, sender: 'user', image: selectedImage }
        ]
      }));

      setMessage('');
      setSelectedImage(null);
      const fileInput = document.getElementById('image-upload');
      if (fileInput) {
        fileInput.value = '';
      }
      setLoading(true);

      setMessages((prevMessages) => ({
        ...prevMessages,
        messages: [
          ...prevMessages.messages,
          { id: Date.now(), sender: 'ai', content: '', isLoading: true }
        ]
      }));

      const response = await chatApi.sendMessage(roomId, message, selectedImage);

      setMessages((prevMessages) => ({
        ...prevMessages,
        messages: prevMessages.messages
          .filter(msg => !msg.isLoading)
          .concat({ id: Date.now(), sender: 'ai', content: response.ai_response })
      }));

      if (response.title) {
        onTitleChange(response.title);
      }

      setLoading(false);
    }
  };


  return (
    <main className="dashboard__main">
      <div id="chat-container" className="dashboard__chat">
        {messages?.messages?.length === 0 ? (
          <div className="dashboard__suggestion">
            <p>It looks like there are no messages yet. Feel free to ask a question!</p>
          </div>
        ) : (
          messages?.messages?.map((msg) => (
            <div key={msg.id} className={`dashboard__message dashboard__message--${msg.sender}`}>
              <div className="dashboard__message-content">
                {msg.image && (
                  <div className="dashboard__message-image">
                    <img 
                      src={typeof msg.image === 'string' ? msg.image : `data:image/${msg.image.type};base64,${msg.image.data}`} 
                      alt="User uploaded" 
                    />
                  </div>
                )}
                {msg.sender === 'ai' ? (
                  <ReactMarkdown components={MarkdownComponents}>{msg.content}</ReactMarkdown>
                ) : (
                  msg.content
                )}
                {msg.isLoading && (
                  <div className="loading-bubble">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      <div className="query-form">
        <form onSubmit={handleSubmit} className="query-form__form">
          <div className="query-form__input-container">
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="query-form__input"
              placeholder="Type your message..."
              rows="1"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
            {selectedImage && (
              <div className="query-form__image-preview">
                <img src={selectedImage} alt="Preview" />
                <button type="button" onClick={clearImage}>✕</button>
              </div>
            )}
            <input
              type="file"
              id="image-upload"
              accept="image/*"
              onChange={handleImageSelect}
              className="query-form__file-input"
            />
            <label htmlFor="image-upload" className="query-form__file-label">
              📷
            </label>
          </div>
          <button type="submit" className="query-form__button" disabled={loading}>
            Send
          </button>
        </form>
      </div>
    </main>
  );
}

export default QueryForm; 